/**
 * Яндекс.Метрика Provider
 * Enhanced Ecommerce implementation
 * 
 * Документация: https://yandex.ru/support/metrica/ecommerce/data.html
 */

import { getDefaultCurrency, getYMId, isDebugMode } from '../config'

import type {
  AddPaymentInfoParams,
  AddShippingInfoParams,
  AddToCartParams,
  AddToWishlistParams,
  AnalyticsProduct,
  AnalyticsProvider,
  ApplyPromoCodeParams,
  BeginCheckoutParams,
  LoginParams,
  PurchaseParams,
  RefundParams,
  RemoveFromCartParams,
  SearchParams,
  SelectItemParams,
  SignUpParams,
  ViewCartParams,
  ViewItemListParams,
  ViewItemParams,
} from '../types'

/**
 * Получить ID счётчика Яндекс.Метрики
 */
function getCounterId (): number | null {
  return getYMId()
}

/**
 * Преобразование товара в формат Яндекс.Метрика Ecommerce
 */
function toYMProduct (product: AnalyticsProduct, index?: number): YMEcommerceProduct {
  return {
    id: product.id,
    name: product.name,
    price: product.price,
    quantity: product.quantity ?? 1,
    ...(product.brand && { brand: product.brand }),
    ...(product.category && { category: product.category }),
    ...(product.variant && { variant: product.variant }),
    ...(index !== undefined && { position: index + 1 }),
    ...(product.listName && { list: product.listName }),
  }
}

/**
 * Отправка ecommerce события в dataLayer
 */
function pushEcommerce (action: string, data: YMEcommerceData): void {
  try {
    if (typeof window === 'undefined') return
    
    window.dataLayer = window.dataLayer || []
    const { currencyCode, ...actionData } = data
    
    const ecommerceEvent = {
      ecommerce: {
        currencyCode: currencyCode || getDefaultCurrency(),
        [action]: actionData,
      },
    }
    
    window.dataLayer.push(ecommerceEvent)
    
    if (isDebugMode()) {
      console.log(`[YM Ecommerce] ${action}`, ecommerceEvent)
    }
  }
  catch (error) {
    console.error('[YM] Error sending ecommerce event:', error)
  }
}

/**
 * Безопасный вызов ym для целей
 */
function safeYm (method: string, ...args: unknown[]): void {
  try {
    if (typeof window !== 'undefined' && window.ym) {
      const counterId = getCounterId()
      if (counterId) {
        window.ym(counterId, method, ...args)
        
        if (isDebugMode()) {
          console.log(`[YM] ${method}`, ...args)
        }
      }
    }
  }
  catch (error) {
    console.error('[YM] Error:', error)
  }
}

/**
 * Яндекс.Метрика Provider
 */
export const ymProvider: AnalyticsProvider = {
  name: 'Яндекс.Метрика',

  init () {
    if (isDebugMode()) {
      console.log('[YM] Provider initialized')
    }
  },

  isAvailable (): boolean {
    return typeof window !== 'undefined' && typeof window.ym === 'function'
  },

  // ========================================================================
  // Ecommerce события
  // ========================================================================

  viewItemList ({ listName, items }: ViewItemListParams) {
    pushEcommerce('impressions', {
      products: items.map((item, index) => ({
        ...toYMProduct(item, index),
        list: listName,
      })),
    })
  },

  viewItem ({ item, currency = getDefaultCurrency() }: ViewItemParams) {
    pushEcommerce('detail', {
      currencyCode: currency,
      products: [ toYMProduct(item) ],
    })
  },

  selectItem ({ listName, item }: SelectItemParams) {
    pushEcommerce('click', {
      actionField: { list: listName },
      products: [ toYMProduct(item) ],
    })
  },

  addToCart ({ item, currency = getDefaultCurrency() }: AddToCartParams) {
    pushEcommerce('add', {
      currencyCode: currency,
      products: [ toYMProduct(item) ],
    })
    
    // Дополнительно отправляем цель
    safeYm('reachGoal', 'add_to_cart', {
      product_id: item.id,
      product_name: item.name,
      price: item.price,
      quantity: item.quantity ?? 1,
    })
  },

  removeFromCart ({ item, currency = getDefaultCurrency() }: RemoveFromCartParams) {
    pushEcommerce('remove', {
      currencyCode: currency,
      products: [ toYMProduct(item) ],
    })
    
    safeYm('reachGoal', 'remove_from_cart', {
      product_id: item.id,
    })
  },

  viewCart ({ items, currency = getDefaultCurrency() }: ViewCartParams) {
    // YM не имеет отдельного события view_cart,
    // отправляем как impressions с пометкой cart
    pushEcommerce('impressions', {
      currencyCode: currency,
      products: items.map((item, index) => ({
        ...toYMProduct(item, index),
        list: 'cart',
      })),
    })
    
    safeYm('reachGoal', 'view_cart', {
      items_count: items.length,
    })
  },

  beginCheckout ({ items, currency = getDefaultCurrency(), coupon }: BeginCheckoutParams) {
    pushEcommerce('checkout', {
      currencyCode: currency,
      actionField: { 
        step: 1,
        ...(coupon && { option: coupon }),
      },
      products: items.map((item, index) => toYMProduct(item, index)),
    })
    
    safeYm('reachGoal', 'begin_checkout', {
      items_count: items.length,
    })
  },

  addShippingInfo ({ items, currency = getDefaultCurrency(), shippingTier }: AddShippingInfoParams) {
    pushEcommerce('checkout', {
      currencyCode: currency,
      actionField: { 
        step: 2,
        ...(shippingTier && { option: shippingTier }),
      },
      products: items.map((item, index) => toYMProduct(item, index)),
    })
    
    safeYm('reachGoal', 'add_shipping_info', {
      shipping_tier: shippingTier,
    })
  },

  addPaymentInfo ({ items, currency = getDefaultCurrency(), paymentType }: AddPaymentInfoParams) {
    pushEcommerce('checkout', {
      currencyCode: currency,
      actionField: { 
        step: 3,
        ...(paymentType && { option: paymentType }),
      },
      products: items.map((item, index) => toYMProduct(item, index)),
    })
    
    safeYm('reachGoal', 'add_payment_info', {
      payment_type: paymentType,
    })
  },

  purchase ({ transactionId, items, currency = getDefaultCurrency(), value, shipping, coupon }: PurchaseParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    pushEcommerce('purchase', {
      currencyCode: currency,
      actionField: {
        id: transactionId,
        revenue: totalValue,
        ...(shipping !== undefined && { shipping }),
        ...(coupon && { coupon }),
      },
      products: items.map((item, index) => toYMProduct(item, index)),
    })
    
    safeYm('reachGoal', 'purchase', {
      order_id: transactionId,
      revenue: totalValue,
      items_count: items.length,
    })
  },

  refund ({ transactionId, items, currency = getDefaultCurrency(), value }: RefundParams) {
    pushEcommerce('refund', {
      currencyCode: currency,
      actionField: {
        id: transactionId,
        ...(value !== undefined && { revenue: value }),
      },
      ...(items && { products: items.map((item, index) => toYMProduct(item, index)) }),
    })
    
    safeYm('reachGoal', 'refund', {
      order_id: transactionId,
    })
  },

  // ========================================================================
  // Кастомные события (через reachGoal)
  // ========================================================================

  search ({ searchTerm, resultsCount }: SearchParams) {
    safeYm('reachGoal', 'search', {
      search_term: searchTerm,
      results_count: resultsCount,
    })
  },

  login ({ method }: LoginParams) {
    safeYm('reachGoal', 'login', {
      method,
    })
  },

  signUp ({ method }: SignUpParams) {
    safeYm('reachGoal', 'sign_up', {
      method,
    })
  },

  addToWishlist ({ item }: AddToWishlistParams) {
    safeYm('reachGoal', 'add_to_wishlist', {
      product_id: item.id,
      product_name: item.name,
      price: item.price,
    })
  },

  applyPromoCode ({ coupon, success, discount }: ApplyPromoCodeParams) {
    safeYm('reachGoal', 'apply_promo_code', {
      coupon,
      success,
      discount,
    })
  },

  // ========================================================================
  // Утилиты
  // ========================================================================

  trackEvent (eventName: string, params?: Record<string, unknown>) {
    safeYm('reachGoal', eventName, params)
  },

  setUserId (userId: string | null) {
    if (userId) {
      safeYm('setUserID', userId)
    }
  },

  setUserProperties (properties: Record<string, unknown>) {
    safeYm('userParams', properties)
  },
}

// ============================================================================
// YM Ecommerce Types
// ============================================================================

interface YMEcommerceProduct {
  id: string
  name: string
  price?: number
  quantity?: number
  brand?: string
  category?: string
  variant?: string
  position?: number
  list?: string
}

interface YMEcommerceData {
  currencyCode?: string
  products?: YMEcommerceProduct[]
  actionField?: {
    id?: string
    revenue?: number
    shipping?: number
    coupon?: string
    step?: number
    option?: string
    list?: string
  }
}

export default ymProvider
